import { createContext, useContext } from 'react';

import type { SearchableVendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import type {
  StorefrontCardView,
  WeddingCardView,
  SeoContentView,
  SearchLocationView,
  USCityView,
  SearchLocationReviewListView,
  SeoSrpMetricDataView,
} from '@zola/svc-marketplace-ts-types';

import type { VendorCardVendor } from '~/components/common/cards/util/types';
import type {
  AccountVendorPreferencesView,
  AccountWeddingPreferencesView,
  SaveAccountWeddingPreferencesRequest,
  TaxonomyWithFacetsView,
  UpdateAccountVendorPreferenceRequest,
  VendorCardView,
} from '~/types/responseTypes';
import { OptionFacetCategoryType } from '~/types/types';
import { FacetsByVendorType } from '~/util/facetUtils';

import type { ParseVendorSearchUrlResult } from '../../../util/parseVendorSearchUrl';
import type { PageCardView } from '../../ExpertAdvice/ExpertAdvice';
import type { VendorSearchRequest, ZolaTopPickCategory } from '../types/types';

/**
 * Result (mapped) from an API search response.
 */
export type SearchResultPageSearchResult = {
  /** How many total matches were there (may be an approximate number) when the search was executed */
  totalHits: number;

  /** Current offset into the total set of search results */
  offset: number;

  /** Storefronts found in the search */
  vendors: StorefrontCardView[];

  /** Flag, if true, indicating that the mini quiz should be inserted on the first page */
  shouldInsertMiniQuiz: boolean;

  /** Flag, if true, indicating that the budget quiz should be inserted on the first page */
  shouldInsertBudgetQuiz: boolean;
};

/**
 * These properties are fetched once and never change, no matter the category/filter/location.
 */
export type SearchResultsPageStaticProps = {
  preAuthSrpProsAndCons: boolean;
  preAuthSrpTopPicks: boolean;
};

const DEFAULT_STATIC_SEARCH_RESULT_PAGE_PROPS: SearchResultsPageStaticProps = {
  preAuthSrpProsAndCons: false,
  preAuthSrpTopPicks: false,
};

/**
 * The properties are dependent on the category/location, but have no dependency on
 * the user context.
 */
export type SearchResultsPageSearchDependentProps = {
  taxonomiesWithFacets: TaxonomyWithFacetsView[];
  facetsByVendorType: FacetsByVendorType;
  /** Search filters for the category */
  searchFilters: OptionFacetCategoryType[];
  /** Backend generated SEO content */
  seoContent: SeoContentView | null;
  /** List of top locations for generating SEO cross links */
  seoTopLocations: SearchLocationView[];
  /** List of nearby cities for generating SEO cross links */
  seoNearbyCities: USCityView[];
  /** SEO metrics to determine if we should index */
  seoMetrics: SeoSrpMetricDataView | null;
  /** List of vendors in adjacent markets for showing in an shelf on the SRP for SEO purposes*/
  adjacentVendors: StorefrontCardView[];
  /** Real weddings from this location to show on a shelf for SEO purposes */
  realWeddings: WeddingCardView[];
  /** EA articles related to this search for SEO purposes */
  expertAdvice: PageCardView[];

  /** Top picks (by zola, not specific to the couple) of vendors */
  zolaTopPicks: { category: ZolaTopPickCategory; vendors: StorefrontCardView[] }[];

  /** Highlighted reviews for the SRP  */
  searchLocationReviews: SearchLocationReviewListView | null;
};

export const DEFAULT_PARSE_VENDOR_SEARCH_URL_RESULT: ParseVendorSearchUrlResult = {
  canonicalUrl: '',
  seoCanonicalUrl: '',
  vendorSearch: {
    page: 1,
  },
  selectedFacetOptions: [],
};

const DEFAULT_SEARCH_DEPENDENT_SEARCH_RESULT_PAGE_PROPS: SearchResultsPageSearchDependentProps = {
  taxonomiesWithFacets: [],
  facetsByVendorType: {},
  searchFilters: [],
  seoContent: null,
  seoTopLocations: [],
  seoNearbyCities: [],
  seoMetrics: null,
  adjacentVendors: [],
  realWeddings: [],
  expertAdvice: [],
  zolaTopPicks: [],
  searchLocationReviews: null,
};

export type RecentlyViewedByTaxonomy = {
  [key in SearchableVendorTaxonomyKey | '']?: VendorCardView[];
};

export type ShouldInsertMiniQuizByTaxonomy = {
  [key in SearchableVendorTaxonomyKey]?: boolean;
};

/**
 * The properties are dependent on the user context, but have no dependency on
 * the location/category/filter
 */
export type SearchResultsPageUserDependentProps = {
  /**
   * If the couple has answered the mini-quiz, or onboarded, this has their budget
   * (and other) preferences for the category.
   */
  vendorPreferences: AccountVendorPreferencesView[];

  /**
   * Wedding preferences, if any for the couple (wedding location)
   */
  weddingPreferences: AccountWeddingPreferencesView | null;

  isSRPLinkExperiment?: boolean;
};

const DEFAULT_USER_DEPENDENT_SEARCH_RESULT_PAGE_PROPS: SearchResultsPageUserDependentProps = {
  vendorPreferences: [],
  weddingPreferences: null,
  isSRPLinkExperiment: false,
};

export type SearchResultsPageUserAndSearchDependentProps = {
  /** Should we show top picks on this search result pages */
  showTopPicks: boolean;
  /** If showing top picks on this search results page, what are they? */
  topPicks: StorefrontCardView[];

  recentlyViewed: VendorCardVendor[];

  /**
   * Current search results (Vendors, total hits, etc)
   */
  searchResults: SearchResultPageSearchResult;

  /**
   * Are we loading new data after a search change?  Use this to blur page elements / provide user feedback.
   */
  loading: boolean;
};
const DEFAULT_USER_AND_SEARCH_DEPENDENT_SEARCH_RESULT_PAGE_PROPS: SearchResultsPageUserAndSearchDependentProps =
  {
    showTopPicks: false,
    topPicks: [],
    recentlyViewed: [],

    searchResults: {
      totalHits: 0,
      offset: 0,
      vendors: [],
      shouldInsertBudgetQuiz: false,
      shouldInsertMiniQuiz: false,
    },
    loading: false,
  };

export type SearchResultPageMutators = {
  setVendorSearch: (vendorSearch: VendorSearchRequest) => void;

  updateWeddingPreferences: (
    weddingPreferencesUuid: string | null,
    values: SaveAccountWeddingPreferencesRequest
  ) => void;
  updateVendorPreferences: (
    vendorPreferencesUuid: string | null,
    values: UpdateAccountVendorPreferenceRequest
  ) => void;
};
const DEFAULT_SEARCH_RESULT_PAGE_MUTATORS: SearchResultPageMutators = {
  setVendorSearch: () => undefined,
  updateWeddingPreferences: () => undefined,
  updateVendorPreferences: () => undefined,
};

export type SearchResultsPageContextValues = ParseVendorSearchUrlResult &
  SearchResultsPageStaticProps &
  SearchResultsPageSearchDependentProps &
  SearchResultsPageUserDependentProps &
  SearchResultsPageUserAndSearchDependentProps &
  SearchResultPageMutators;

export const DEFAULT_SEARCH_RESULTS_CONTEXT_VALUES: SearchResultsPageContextValues = {
  ...DEFAULT_PARSE_VENDOR_SEARCH_URL_RESULT,
  ...DEFAULT_STATIC_SEARCH_RESULT_PAGE_PROPS,
  ...DEFAULT_SEARCH_DEPENDENT_SEARCH_RESULT_PAGE_PROPS,
  ...DEFAULT_USER_DEPENDENT_SEARCH_RESULT_PAGE_PROPS,
  ...DEFAULT_USER_AND_SEARCH_DEPENDENT_SEARCH_RESULT_PAGE_PROPS,
  ...DEFAULT_SEARCH_RESULT_PAGE_MUTATORS,
};

export const SearchResultsPageContext = createContext<SearchResultsPageContextValues | undefined>(
  undefined
);

export const useSearchResultsPageContext = () => {
  const context = useContext(SearchResultsPageContext);
  if (context === undefined) {
    throw new Error('useSearchResultsPageContext must be used within a SearchResultsPageContext');
  }
  return context;
};
